import { useEffect, useState } from 'react';

import InventoryAssetTree from '@atom/components/common/inventoryAssetTree/InventoryAssetTree';
import { useInventoryCategoryTree } from '@atom/hooks/useInventoryCategoryTree';
import { Button, Modal, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import {
  getAllDescendantCategoryIds,
  getCategoriesByIds,
} from '@atom/utilities/categoryUtilities';

import classes from './filterPanel.module.css';

const styles = {
  button: {
    minWidth: 0,
    padding: 0,
    fontSize: fonts.sm,
    fontWeight: 'normal',
    textTransform: 'capitalize',
  },
  progress: {
    justifyContent: 'flex-start',
  },
  modalContent: {
    height: '50vh',
    padding: '0',
  },
};

type Category = {
  id: string;
  name: string;
  categories?: Category[];
};

type InventoryFolderFilterProps = {
  categoryIds: Set<string>;
  toggled?: boolean;
  onCategoryIdsUpdate: (ids: Set<string>) => void;
};

const InventoryFolderFilter = ({
  categoryIds,
  toggled,
  onCategoryIdsUpdate,
}: InventoryFolderFilterProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<Category[]>([]);
  const [applied, setApplied] = useState<Category[]>([]);

  const [categoryTree, { loading, refetch }] = useInventoryCategoryTree({
    lazy: true,
  });

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (categoryTree) {
      const categories = getCategoriesByIds([...categoryIds], [categoryTree]);

      setSelected(categories);
      setApplied(categories);
    }
  }, [categoryTree]);

  useEffect(() => {
    const categories = selected.filter(({ id }) => categoryIds.has(id));

    setSelected(categories);
    setApplied(categories);
  }, [categoryIds]);

  const handleToggle = (category: Category) => {
    const isSelected = selected.some(({ id }) => category.id === id);

    setSelected(
      isSelected
        ? selected.filter(({ id }) => id !== category.id)
        : [...selected, category],
    );
  };

  const handleSave = () => {
    const descendantIds = new Set(
      selected.reduce((acc: string[], category): string[] => {
        return [...acc, ...getAllDescendantCategoryIds(category)];
      }, []),
    );

    const newCategoryIds = selected
      .map(({ id }) => id)
      .filter(id => !descendantIds.has(id));

    onCategoryIdsUpdate(new Set(newCategoryIds));
    setApplied(selected);
    setOpen(false);
  };

  const handleReset = () => {
    onCategoryIdsUpdate(new Set([]));
  };

  const handleCancel = () => {
    setSelected(applied);
    setOpen(false);
  };

  const handleClear = () => {
    setSelected([]);
  };

  const selectedCategoryDisplay = applied
    .filter(({ id }) => categoryIds.has(id))
    .map(({ name }) => name)
    .join(', ');
  const selectedIds = new Set(selected.map(({ id }) => id));
  const disabled = loading || !toggled;
  const buttonStyles = {
    ...styles.button,
    color: toggled ? colors.brand.blue : colors.neutral.gray,
  };

  return (
    <>
      <div className={classes.filterContainerLabel}>Inventory Folder</div>
      <div className={classes.buttonContainer}>
        <Button
          style={buttonStyles}
          size="small"
          onClick={() => setOpen(true)}
          disabled={disabled}
          data-cy="mapFilterInventoryFolderSelect"
        >
          select
        </Button>
        {categoryIds.size > 0 && (
          <>
            <span className={classes.buttonDivider}>|</span>
            <Button
              style={buttonStyles}
              size="small"
              disabled={disabled}
              onClick={handleReset}
              data-cy="mapFilterInventoryFolderClear"
            >
              clear all
            </Button>
          </>
        )}
      </div>
      <div className={classes.filterValues}>
        {loading && <Progress size={20} style={styles.progress} />}
        {categoryIds.has('inventory')
          ? 'All Inventory Folders'
          : selectedCategoryDisplay}
      </div>
      <Modal
        title="Select Inventory Folder"
        open={open}
        onCancel={handleCancel}
        onConfirm={handleSave}
        onClear={handleClear}
        contentStyle={styles.modalContent}
      >
        <InventoryAssetTree
          includeAssets={false}
          schemaIds={[]}
          selectedCategoryIds={selectedIds}
          onCategoryToggle={handleToggle}
          autoSelectChildCategories
          showRootCategoryCheckbox
          includeLocationBased
        />
      </Modal>
    </>
  );
};

export default InventoryFolderFilter;
